import { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';

import { assertValue } from '@libs/utils';
import { trackDeserializeIngredientWithEncodedPlusSign } from '@libs/feature-toggle';
import { IngredientType } from '@libs/trip-planner-interfaces';

import {
  DESTINATION_PRODUCTS_PREFIX,
  DISPLAY_TEXT_PREFIX,
  CONSTRAINT_TEXT_SUFFIX,
} from '../constants';
import { ActivitySerializer } from '../../activitiySerializer';
import {
  deserializeNumberOfNights,
  deserializeTransportType,
} from '../deserializeStay';

import { OWN_STAY_INGREDIENT_SUFFIX, PROPS_PREFIX } from './constants';

export function deserializeOwnStay(value: string): OwnStayIngredient {
  const { constraintText, destinationProducts, numberOfNights, transportType } =
    deserializeIngredientParameters(value);

  const { displayText, searchText } = deserializeConstraintText(constraintText);

  const result = new OwnStayIngredient(searchText, displayText);

  destinationProducts.forEach((product) => {
    result.subIngredients.push(
      ActivitySerializer.deserialize(product, result.ingredientId)
    );
  });

  result.numberOfNights = deserializeNumberOfNights(numberOfNights);

  result.transportTypeToBeReached = deserializeTransportType(transportType);

  return result;
}

export interface SerializedOwnStayParameters {
  constraintText: string;
  destinationProducts: string[];
  numberOfNights: string;
  transportType: string;
}

export function deserializeIngredientParameters(
  value: string
): SerializedOwnStayParameters {
  if (value.includes(encodeURIComponent('+'))) {
    trackDeserializeIngredientWithEncodedPlusSign(IngredientType.OwnStay);
  }

  const sanitizedValue = decodeURIComponent(value)
    .replace(/\+/g, ' ')
    .replace(OWN_STAY_INGREDIENT_SUFFIX, '');

  const destinationProductsIndex = sanitizedValue.indexOf(
    DESTINATION_PRODUCTS_PREFIX
  );

  const propsIndex = sanitizedValue.indexOf(PROPS_PREFIX);

  const constraintTextEndIndex =
    destinationProductsIndex !== -1 ? destinationProductsIndex : propsIndex;

  const constraintText = sanitizedValue.slice(0, constraintTextEndIndex);

  const destinationProducts = sanitizedValue
    .slice(destinationProductsIndex, propsIndex)
    .split(DESTINATION_PRODUCTS_PREFIX)
    .filter(Boolean);

  const props = sanitizedValue.slice(propsIndex).replace(PROPS_PREFIX, '');

  const numberOfNights = props.slice(0, 2);

  const transportType = props.slice(2);

  return {
    constraintText,
    destinationProducts,
    numberOfNights,
    transportType,
  };
}

export function deserializeConstraintText(constraintText: string): {
  displayText: string;
  searchText: string;
} {
  const result: {
    displayText: string;
    searchText: string;
  } = {
    displayText: '',
    searchText: '',
  };

  const displayTextRegExp = new RegExp(
    `(${DISPLAY_TEXT_PREFIX}(.*?)${CONSTRAINT_TEXT_SUFFIX}).*$`
  );
  const searchTextRegExp = new RegExp(
    `(?:[^${CONSTRAINT_TEXT_SUFFIX}](?!(${CONSTRAINT_TEXT_SUFFIX})))+$`
  );

  const displayTextMatch = constraintText.match(displayTextRegExp);
  const searchTextMatch = constraintText.match(searchTextRegExp);

  assertValue(
    searchTextMatch,
    'ownStaySerializer.ts > deserializeConstraintText > searchTextMatch'
  );

  const matchedSearchText = searchTextMatch[0];

  result.displayText = matchedSearchText;

  result.searchText = matchedSearchText;

  if (displayTextMatch) {
    const matchedDisplatText = displayTextMatch[2];

    assertValue(
      matchedDisplatText,
      'ownStaySerializer.ts > deserializeConstraintText > matchedDisplatText'
    );

    result.displayText = matchedDisplatText;
  }

  return result;
}
